import { Component, OnInit, signal, ViewChild } from '@angular/core';
import { MemberRole, Project, ProjectType } from 'domain-entities';
import { ProjectAreaStore } from './store/project-area.store';
import { ProjectContext } from '@shared/models/project.type';
import { Store } from '@ngrx/store';
import {
	selectActiveProject,
	selectIsProjectArchiveRoute,
	selectWorkRouteState,
} from '@store/selectors/route.selectors';
import { map, take } from 'rxjs/operators';
import { combineLatest, firstValueFrom, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { selectProjectStatus, selectUserRole } from '@store/selectors/app.selectors';
import { GroupByOption } from '@work/project-search/types/project-sort.types';
import { selectProject, selectSelectedFolderId } from '@store/selectors/projects.selectors';
import { setSelectedFolderAction } from '@store/actions/project.actions';
import { ProjectListStoreUpstreamData } from '@modules/shared/components/projects-area/components/projects-list/store/project-list.store';
import { toObservable } from '@angular/core/rxjs-interop';
import { selectProjectsWithFailedUploads } from '@store/selectors/file-upload.selectors';
import { ProjectsListComponent } from './components/projects-list/projects-list.component';

@Component({
	selector: 'app-projects-area',
	templateUrl: './projects-area.component.html',
	styleUrls: ['./projects-area.component.scss'],
	providers: [ProjectAreaStore],
})
export class ProjectsAreaComponent implements OnInit {
	filteredProjects = signal<Project[]>([]);
	projects$ = this.componentStore.openFolderProjects$;
	openFolder$ = this.componentStore.openFolder$;
	isArchive$ = this.store.select(selectIsProjectArchiveRoute);
	context$: Observable<ProjectContext> = this.store
		.select(selectWorkRouteState)
		.pipe(
			map((state) =>
				state.workRoute === 'projects' ? ProjectContext.ACTIVE : ProjectContext.ARCHIVE,
			),
		);
	selectedProjectId$ = this.store.select(selectActiveProject);

	GroupByOption = GroupByOption;
	groupByOption = signal<GroupByOption>(GroupByOption.NONE);

	isOnActiveRootLevel$ = combineLatest([this.context$, this.openFolder$]).pipe(
		map(([context, openFolder]) => context === ProjectContext.ACTIVE && !openFolder),
	);

	canShowArchivationNudge$ = combineLatest([
		this.isOnActiveRootLevel$,
		this.store.select(selectUserRole),
	]).pipe(map(([isOnActiveRootLevel, role]) => isOnActiveRootLevel && role === MemberRole.OWNER));

	projectListStoreData$: Observable<ProjectListStoreUpstreamData> = combineLatest([
		toObservable(this.filteredProjects),
		this.componentStore.archivableProjects$,
		this.store.select(selectProjectsWithFailedUploads),
		this.isOnActiveRootLevel$,
		this.canShowArchivationNudge$,
		this.store.select(selectProjectStatus),
		toObservable(this.groupByOption),
	]).pipe(
		map(
			([
				projects,
				archivableProjects,
				projectsWithFailedUploads,
				canShowFailedUploadsNudge,
				canShowArchivationNudge,
				projectStatus,
				groupBy,
			]) => ({
				projects,
				archivableProjects,
				projectsWithFailedUploads,
				canShowFailedUploadsNudge,
				canShowArchivationNudge,
				projectStatus,
				groupBy,
			}),
		),
	);

	ProjectContext = ProjectContext;
	isFilterActive: boolean;
	@ViewChild(ProjectsListComponent) projectsListComponent: ProjectsListComponent;

	constructor(
		private readonly componentStore: ProjectAreaStore,
		private readonly store: Store,
		private readonly router: Router,
	) {}

	ngOnInit(): void {
		this.initOpenFolder();
	}

	async onProjectClick(project: Project, action: 'open' | 'back' = 'open'): Promise<void> {
		if (action === 'back') {
			const parentFolderId = this.getParentFolderId(project);
			this.componentStore.openFolder(parentFolderId);
			setTimeout(() => {
				this.projectsListComponent.setCurrentFolder(parentFolderId);
			});
		} else if (project.projectType === ProjectType.FOLDER) {
			this.componentStore.openFolder(project.id);
			setTimeout(() => {
				this.projectsListComponent.setCurrentFolder(project.id);
			});
		} 

		try {
			const currentProject = await firstValueFrom(
				this.store.select(selectActiveProject).pipe(take(1)),
			);
			const isArchiveRoute = await firstValueFrom(
				this.store.select(selectIsProjectArchiveRoute).pipe(take(1)),
			);

			// Check if we're in the files section
			const isInFilesSection = this.router.url.includes('/files');
			
			// Your solution that works - navigate to base project URL first when in files section
			if (isInFilesSection) {
				const baseRoute = isArchiveRoute ? 'archive' : 'projects';
				// First navigate to the base project path, without the files section
				// This avoids router state conflicts that may occur in production
				const navigationResult = await this.router.navigate([baseRoute, project.id], { replaceUrl: true });
				
				// Then immediately navigate to add the files section back
				if (navigationResult) {
					// Extract the files path (including any subfolders)
					const filesPath = this.router.url.split('/files')[1] || '';
					// After a slight delay to ensure router state is updated, navigate to the files section
					setTimeout(() => {
						this.router.navigate([baseRoute, project.id, 'files' + filesPath], { replaceUrl: true });
					}, 10);
				}
				return;
			}
			
			let newUrl: boolean;
			if (!currentProject) {
				newUrl = await this.router.navigate([
					isArchiveRoute ? 'archive' : 'projects',
					project.id,
				]);
			} else {
				// Simple URL replacement - this worked in the original version
				newUrl = await this.router.navigate([
					this.router.url.replace(currentProject, project.id),
				]);
			}

			if (newUrl === false) {
				console.error('Navigation failed');
			}
		} catch (error) {
			console.error('Error during project navigation:', error);
		}
	}

	groupBy(groupByOption: GroupByOption): void {
		this.groupByOption.set(groupByOption);
	}

	/**
	 * When the component is created we are checking ONCE if the currently open project is inside a folder and
	 * if so we are trying to open that folder if by the time we resolved the open project no other folder has been
	 * opened in the meantime.
	 */
	private async initOpenFolder(): Promise<void> {
		const projectIdAtStart = await this.selectedProjectId$.pipe(take(1)).toPromise();
		if (!projectIdAtStart) {
			return;
		}

		const project = await this.store
			.select(selectProject, { projectId: projectIdAtStart })
			.pipe(take(1))
			.toPromise();

		const selectedProjectAfterResolution = await this.selectedProjectId$.pipe(take(1)).toPromise();

		const currentOpenFolder = await this.store
			.select(selectSelectedFolderId)
			.pipe(take(1))
			.toPromise();

		if (
			project?.parentProject &&
			project?.id === selectedProjectAfterResolution &&
			!currentOpenFolder
		) {
			this.store.dispatch(setSelectedFolderAction({ folderId: project?.parentProject }));
		}
	}

	private getParentFolderId(project: Project): string | null {
		return project.parentProject || null;
	}
}
